import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Star, Loader2, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const CATEGORIES = [
  { key: "products", label: "Products", hint: "Quality of what you bought or saw" },
  { key: "prices", label: "Prices", hint: "Fair for the value?" },
  { key: "customer_experience", label: "Customer experience", hint: "How the vendor treated you" },
  { key: "setup", label: "Booth setup", hint: "Presentation, cleanliness, layout" },
];

/**
 * Public, unauthenticated customer rating page. A vendor puts up a QR code
 * at their booth; a customer scans it, taps stars, submits. Only the vendor
 * hopes for glowing reviews — everyone else hopes for honesty.
 */
export default function RateVendor() {
  const { slug } = useParams();
  const [vendor, setVendor] = useState(null);
  const [existing, setExisting] = useState(null);
  const [scores, setScores] = useState({});
  const [comment, setComment] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/customer-ratings/${slug}`)
      .then((r) => { setVendor(r.data); setExisting(r.data); })
      .catch(() => setVendor({ error: true }));
  }, [slug]);

  const setStar = (key, value) => setScores((s) => ({ ...s, [key]: value }));

  const rated = Object.values(scores).filter((v) => v >= 1 && v <= 5).length;

  const submit = async (e) => {
    e.preventDefault();
    if (rated === 0) {
      toast.error("Rate at least one category.");
      return;
    }
    setSaving(true);
    try {
      const rated_dims = Object.fromEntries(
        Object.entries(scores).filter(([, v]) => v)
      );
      await axios.post(`${BACKEND}/api/customer-ratings/${slug}`, {
        ...rated_dims,
        comment,
        display_name: displayName,
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === "string" ? msg : "Could not submit rating");
    } finally { setSaving(false); }
  };

  if (!vendor) {
    return <div className="min-h-screen flex items-center justify-center"
                 data-testid="rate-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  if (vendor.error) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="rate-not-found">
        <h1 className="font-serif text-3xl">Vendor not found</h1>
        <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>
          The link on that QR code doesn't match any vendor. Ask them to check their profile URL.
        </p>
        <Link to="/" className="mt-6 inline-block underline text-sm">Back to AddVendTour</Link>
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="rate-submitted">
        <CheckCircle2 className="h-10 w-10 mx-auto" style={{ color: "var(--forest)" }} />
        <h1 className="font-serif text-3xl mt-3">Thank you!</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
          Your feedback is on its way to <strong>{vendor.vendor_name}</strong>. Ratings help great
          vendors get discovered and help everyone else improve.
        </p>
        <Link to="/" className="mt-6 inline-block underline text-sm">Explore other makers</Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-12" data-testid="rate-page">
      <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--terracotta)" }}>
        <Star className="h-3 w-3" /> Rate this vendor
      </div>
      <h1 className="font-serif text-4xl mt-2">{vendor.vendor_name || "Vendor"}</h1>
      {existing?.count > 0 && (
        <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
          {existing.count} previous rating{existing.count === 1 ? "" : "s"} · currently {existing.overall}★ overall
        </p>
      )}
      <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>
        Honest feedback in 30 seconds. Skip categories you can't judge.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="rate-form">
        {CATEGORIES.map((c) => (
          <div key={c.key} data-testid={`rate-row-${c.key}`}>
            <div className="flex items-baseline justify-between">
              <label className="font-medium">{c.label}</label>
              <span className="text-xs" style={{ color: "var(--ink-soft)" }}>{c.hint}</span>
            </div>
            <div className="mt-2 flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  data-testid={`rate-${c.key}-${n}`}
                  onClick={() => setStar(c.key, n)}
                  className="p-1.5 transition-transform hover:scale-110"
                  aria-label={`${c.label} ${n} star${n > 1 ? "s" : ""}`}
                >
                  <Star
                    className="h-7 w-7"
                    style={{
                      color: (scores[c.key] || 0) >= n ? "var(--gold)" : "rgba(0,0,0,0.15)",
                      fill: (scores[c.key] || 0) >= n ? "var(--gold)" : "transparent",
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        ))}

        <div>
          <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Your name (optional)</label>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                  data-testid="rate-display-name" maxLength={60}
                  placeholder="Casey" />
        </div>
        <div>
          <label className="text-xs" style={{ color: "var(--ink-soft)" }}>Anything else? (optional)</label>
          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
                     data-testid="rate-comment"
                     placeholder="Loved the ceramics — will definitely be back."
                     maxLength={1000} />
        </div>

        <Button type="submit" disabled={saving || rated === 0}
                 data-testid="rate-submit"
                 className="btn-primary rounded-full w-full">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
          Submit rating
        </Button>
        <div className="text-xs text-center" style={{ color: "var(--ink-soft)" }}>
          Anonymous · takes 30 seconds · this helps small businesses grow
        </div>
      </form>
    </div>
  );
}
